import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  onAllAdmin,
  onAllRole,
  onCreateAdmin,
  onEditAdmin,
} from "../../redux/homeAction";

const AdminCreate = () => {
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    phone: "",
    name: "",
    email: "",
    gender: "",
    dob: "",
    password: "",
    roles: [],
  });
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [loader, setLoader] = useState(false);
  const [visiblePermissions, setVisiblePermissions] = useState({});
  const getAllRole = useSelector((state) => state.home.getAllRole);

  useEffect(() => {
    dispatch(onAllRole(setLoader, setRoles));
    dispatch(onAllAdmin(setLoader, setUsers));
  }, []);

  useEffect(() => {
    if (getAllRole) {
      const roleOptions = getAllRole?.map((r) => ({
        value: r.name,
        label: r.name,
      }));
      setRoles(roleOptions);
    }
  }, [getAllRole]);

  // Validation
  const validate = () => {
    let newErrors = {};
    if (!form.phone.match(/^[0-9]{10}$/)) {
      newErrors.phone = "Phone number must be 10 digits";
    }
    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.email = "Invalid email format";
    }
    if (!editingId && (!form.password || form.password.length < 6)) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (form.roles.length === 0) {
      newErrors.roles = "At least one role is required";
    }
    return newErrors;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (selectedOptions) => {
    const selectedRoles = selectedOptions.map((opt) => opt.value);
    setForm({ ...form, roles: selectedRoles });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      const token = localStorage.getItem("adminToken");
      if (editingId) {
        // Update
        dispatch(onEditAdmin(setLoader, form, editingId, setMessage));
      } else {
        // Create
        dispatch(onCreateAdmin(setLoader, form, setMessage));
      }
      // resetForm();
    } catch (err) {
      setMessage(err.response?.data?.message || "Error saving user");
    }
  };

  const handleEdit = (user) => {
    setEditingId(user._id);
    setForm({
      phone: user.phone,
      name: user.name,
      email: user.email,
      gender: user.gender || "",
      dob: user.dob ? user.dob.slice(0, 10) : "",
      password: "",
      roles: user.roles || [],
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.delete(`/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(res.data.message);
    } catch (err) {
      setMessage(err.response?.data?.message || "Error deleting user");
    }
  };

  const resetForm = () => {
    setForm({
      phone: "",
      name: "",
      email: "",
      gender: "",
      dob: "",
      password: "",
      roles: [],
    });
    setEditingId(null);
  };

  const togglePermissions = (userId) => {
    setVisiblePermissions((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  return (
    <div className="container mt-5">
      <div className="card shadow p-4">
        <h2 className="text-center mb-4">
          {editingId ? "Edit User/Admin" : "Create New User/Admin"}
        </h2>
        {message && <div className="alert alert-info">{message}</div>}

        {/* FORM */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Phone *</label>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className={`form-control ${errors.phone ? "is-invalid" : ""}`}
              />
              {errors.phone && (
                <div className="invalid-feedback">{errors.phone}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Name *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Email *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Gender</label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Date of Birth</label>
              <input
                type="date"
                name="dob"
                value={form.dob}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">
                Password {editingId ? "(leave blank to keep)" : "*"}
              </label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                className={`form-control ${
                  errors.password ? "is-invalid" : ""
                }`}
              />
              {errors.password && (
                <div className="invalid-feedback">{errors.password}</div>
              )}
            </div>
          </div>

          {/* Roles */}
          <div className="mb-3">
            <label className="form-label">Assign Roles *</label>
            <Select
              isMulti
              options={roles}
              value={roles.filter((r) => form.roles.includes(r.value))}
              onChange={handleRoleChange}
              placeholder="Select roles..."
            />
            {errors.roles && (
              <div className="text-danger mt-1">{errors.roles}</div>
            )}
          </div>

          <button type="submit" className="btn btn-primary w-100">
            {editingId ? "Update User" : "Create User"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="btn btn-secondary w-100 mt-2"
            >
              Cancel Edit
            </button>
          )}
        </form>
      </div>

      {/* USERS TABLE */}
      <div className="card shadow p-4 mt-4">
        <h3 className="mb-3">Users List</h3>
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle">
            <thead className="table-dark">
              <tr>
                <th>Phone</th>
                <th>Name</th>
                <th>Email</th>
                <th>Gender</th>
                <th>Role</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u._id}>
                    <td>{u.phone}</td>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.gender}</td>
                    <td>
                      {u.role.map((r) => (
                        <div key={r._id} className="mb-1">
                          <span className="badge bg-primary me-1">
                            {r.name}
                          </span>
                        </div>
                      ))}
                    </td>
                    <td>
                      {u.role.map((r) => (
                        <div key={r._id} className="mb-2">
                          <button
                            className="btn btn-sm btn-secondary mb-1"
                            onClick={() =>
                              togglePermissions(`${u._id}_${r._id}`)
                            }
                          >
                            {visiblePermissions[`${u._id}_${r._id}`]
                              ? "Hide"
                              : "Show"}
                          </button>
                          {visiblePermissions[`${u._id}_${r._id}`] && (
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {r.permissions.map((perm, idx) => (
                                <span
                                  key={r._id + idx}
                                  className="badge bg-info text-dark"
                                >
                                  {perm}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleEdit(u)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(u._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCreate;
